<script setup>
import PageWrapper from '@/components/shared/page/PageWrapper.vue'
import AgentCard from '@/components/agent/AgentCard.vue'
import AppVersion from '@/components/app/AppVersion.vue'
import FlexColumn from '@/components/shared/page/FlexColumn.vue'
import NoContent from '@/components/shared/page/NoContent.vue'
import Spinner from '@/components/shared/core/Spinner.vue'
import EnrollmentCard from '@/components/enrollments/EnrollmentCard.vue'
import SearchInput from '@/components/shared/form/SearchInput.vue'
import Heading from '@/components/shared/page/Heading.vue'
import Alert from '@/components/shared/core/Alert.vue'
import ActionButton from '@/components/shared/buttons/ActionButton.vue'
import Icon from '@/components/shared/icons/Icon.vue'
import Inflect from '@/components/shared/transform/Inflect.vue'
import {enrollmentFilterStates} from '@/constants/enrollments.js'
import {useEnrollmentsQuerying} from '@/composables/use-enrollments-querying.js'
import {useNavigation} from '@/composables/use-navigation.js'
import {useTeams} from '@/composables/use-teams.js'
import {useTeamsAppIds} from '@/composables/use-teams-app-ids.js'
import {useSearch} from '@/composables/use-search.js'
import {useValueToggle} from '@/composables/use-value-toggle.js'
import {useCurrentOrganization} from '@/composables/use-current-organization.js'
import {useInfiniteScroll} from '@vueuse/core'
import {ref, computed, onMounted} from 'vue'

//Composables
const {isInTeams, teamsAppVersion, promptTeamsAppInstall} = useTeams()
const {teamsAppVersion: teamsAppVersionForOrg, showTeamsAppVersionWarning} = useCurrentOrganization()
const {teamsAppIdV3} = useTeamsAppIds()
const {goToConversationForEnrollment} = useNavigation()

//Teams warning
const shouldShowTeamsAlert = computed(() => (
  isInTeams.value && showTeamsAppVersionWarning.value &&
  teamsAppVersionForOrg.value === 3 && teamsAppVersion.value < 3
))

//Handle Teams app install prompt
const handleTeamsAppInstall = event => {
  event.preventDefault()
  event.stopPropagation()
  promptTeamsAppInstall(teamsAppIdV3)
}

//Enrollment querying
const {
  filter,
  enrollments,
  totalEnrollments,
  loadEnrollments,
  loadMoreEnrollments,
  isLoadingEnrollments,
  isLoadingMoreEnrollments,
  hasLoadedAllEnrollments,
  changeSearch,
  changeFilter,
} = useEnrollmentsQuerying()

//Toggle app version information
const {
  isValue: isShowingAppVersion,
  setTrue: showAppVersion,
} = useValueToggle()

//State filter helper flags
const isFilteringActive = computed(() => filter.value.state === enrollmentFilterStates.ACTIVE)
const isFilteringAll = computed(() => filter.value.state === enrollmentFilterStates.ALL)

//State filter change handler
const setStateFilter = value => {
  if (value !== enrollmentFilterStates.ALL) {
    changeSearch('', false)
  }
  changeFilter({state: value})
}

//State filter toggle
const toggleStateFilter = () => {
  if (isFilteringActive.value) {
    setStateFilter(enrollmentFilterStates.ALL)
  }
  else {
    setStateFilter(enrollmentFilterStates.ACTIVE)
  }
}

//Filtered enrollments, this creates a subset of enrollments where for enrollments
//in a channel, we only show the latest enrollment for that channel, unless the
//enrollment is a nudge. Nudges are always shown separately.
//The backend will already return all enrollments in the correct order, with the
//latest enrollment first (sort=-lastMessageSentAt).
const filteredEnrollments = computed(() => {

  //If filtering all, return all enrollments
  if (isFilteringAll.value) {
    return enrollments.value
  }

  //Filter out duplicate channel enrollments (non-channel enrollments always pass)
  const channelIds = new Set()
  return enrollments.value.filter(enrollment => {
    if (!enrollment.channel || enrollment.isNudge) {
      return true
    }
    if (!channelIds.has(enrollment.channel.id)) {
      channelIds.add(enrollment.channel.id)
      return true
    }
    return false
  })
})

//Updated total enrollments count to factor in filtered enrollment removal
const totalEnrollmentsCount = computed(() => {
  if (isFilteringAll.value) {
    return totalEnrollments.value
  }
  const diff = enrollments.value.length - filteredEnrollments.value.length
  return totalEnrollments.value - diff
})

//Helper to check if we should link the user to the first message of an enrollment
//We do this either when the enrollment is a nudge, or when a user is viewing all
//enrollments/searching for an enrollment.
const shouldScrollToFirstMessage = enrollment => isFilteringAll.value || enrollment.isNudge

//Infinite scroll
const pageWrapper = ref(null)
const contents = computed(() => pageWrapper.value?.contents)
useInfiniteScroll(contents, loadMoreEnrollments, {
  distance: 10,
  canLoadMore: () => !hasLoadedAllEnrollments.value,
})

//Searching (always takes place for all enrollments)
const fullSearchHandler = search => {
  changeFilter({state: enrollmentFilterStates.ALL}, false)
  changeSearch(search)
}
const {
  searchTerm,
  fullSearch,
  clearSearch,
} = useSearch(fullSearchHandler)

//Whether the agent thread has messages (reported by the agent card after it
//loads). The card mounts unconditionally so it can appear even when there are
//no enrollments; this flag keeps the no-content state from showing alongside it.
const hasAgentThread = ref(false)

//Load enrollment on mount
onMounted(loadEnrollments)
</script>

<template>
  <PageWrapper ref="pageWrapper">
    <template #headerLeft>
      <ActionButton
        :type="isFilteringActive ? 'primary' : 'secondary'"
        icon="filter_list"
        @click="toggleStateFilter()"
      />
    </template>

    <template #header>
      <Heading>
        <Inflect
          :text="isFilteringActive ? 'active enrollment' : 'enrollment'"
          :count="totalEnrollmentsCount"
          :include-count="true"
        />
      </Heading>
    </template>

    <div :class="$style.search">
      <SearchInput
        :search-term="searchTerm"
        placeholder="Search all enrollments"
        select-on-focus
        @clear-search="clearSearch()"
        @partial-search="fullSearch($event.searchTerm)"
      />
    </div>

    <Alert
      v-if="shouldShowTeamsAlert"
      type="warning"
    >
      This version of the Arist Teams app will be discontinued shortly. Please <a
        @click="handleTeamsAppInstall($event)"
      >click here to use the new Arist Teams app</a>.
    </Alert>

    <!--
      The list renders unconditionally so the agent card can mount (and appear)
      even when there are no enrollments. The card reports back whether the
      agent thread has messages, which gates the no-content state below.
    -->
    <FlexColumn :class="$style.enrollments">
      <!--
        Agent conversation pinned above the enrollments. It renders nothing
        while the agent thread is empty.
      -->
      <AgentCard @load="hasAgentThread = $event.hasThread" />
      <template v-if="!isLoadingEnrollments">
        <EnrollmentCard
          v-for="enrollment in filteredEnrollments"
          :key="enrollment.id"
          :enrollment="enrollment"
          @click="goToConversationForEnrollment(enrollment, {
            scrollToFirstMessage: shouldScrollToFirstMessage(enrollment)
          })"
        />
        <Spinner
          v-if="isLoadingMoreEnrollments"
          is-block
        />
      </template>
    </FlexColumn>

    <Spinner
      v-if="isLoadingEnrollments"
      is-block
    />

    <!--
      No content state: suppressed when the agent card is visible with no
      enrollments (the pinned card is the content), but kept during an active
      search so a search without matches still gets explicit feedback
    -->
    <NoContent
      v-else-if="enrollments.length === 0 && (!!searchTerm || !hasAgentThread)"
      text="No enrollments"
    />

    <AppVersion v-if="isShowingAppVersion" />
    <Icon
      v-else-if="isInTeams"
      icon="info"
      is-outlined
      size="xs"
      title="Show app version information"
      :class="$style.version"
      @click="showAppVersion()"
    />
  </PageWrapper>
</template>

<style module>
.enrollments {
  gap: 0;
  margin-top: 2rem;
}
.search {
  padding: 1px; /* For outline border on focus */
}
.version {
  margin-top: 1rem;
  color: var(--p-form-field-icon-color);
  cursor: pointer;
}
</style>
